import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { storage } from "../utils/platform";
import { API_BASE } from "../config/api";

export default function Configuration() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nom: "",
    ville: "",
    slug: "",
    google_maps_url: "",
    whatsapp_message: "",
    ticket_moyen: "",
    couverts_semaine: "",
    note_depart: "",
    avis_depart: "",
    delai_envoi_minutes: "",
    objectif_avis: "",
  });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await storage.get("token");
      if (cancelled) return;
      if (!token) { navigate("/login"); return; }
      try {
        const r = await fetch(`${API_BASE}/api/restaurants/config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled) return;
        if (r.status === 401) { navigate("/login"); return; }
        if (r.status === 402) { navigate("/dashboard"); return; }
        if (!r.ok) throw new Error(r.status);
        const data = await r.json();
        if (cancelled) return;
        if (data && !data.error) {
          setForm(prev => ({
            ...prev,
            nom: data.nom ?? "",
            ville: data.ville ?? "",
            slug: data.slug ?? "",
            google_maps_url: data.google_maps_url ?? "",
            whatsapp_message: data.whatsapp_message ?? "",
            ticket_moyen: data.ticket_moyen != null ? String(data.ticket_moyen) : "",
            couverts_semaine: data.couverts_semaine != null ? String(data.couverts_semaine) : "",
            note_depart: data.note_depart != null ? String(data.note_depart) : "",
            avis_depart: data.avis_depart != null ? String(data.avis_depart) : "",
            delai_envoi_minutes: data.delai_envoi_minutes != null ? String(data.delai_envoi_minutes) : "",
            objectif_avis: data.objectif_avis != null ? String(data.objectif_avis) : "",
          }));
        }
        setLoading(false);
      } catch (err) {
        console.error("Config load error:", err);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const token = await storage.get("token");
    if (!token) return;
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/restaurants/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      // Parse défensif : un 502 Railway peut renvoyer du HTML → res.json() crash.
      let data = {};
      try { data = await res.json(); } catch {}

      if (res.status === 401) { navigate("/login"); return; }
      if (res.status === 402) { setError("Votre compte est en pause. Rendez-vous sur le dashboard pour le réactiver."); return; }
      if (!res.ok) { setError(data.error || `Erreur serveur (${res.status}). Réessayez.`); return; }

      if (data.success) { setSaved(true); setTimeout(() => navigate("/dashboard"), 2000); }
      else setError(data.error || "Erreur sauvegarde");
    } catch { setError("Erreur de connexion"); }
  };

  if (loading) return <div style={{textAlign:"center",padding:"40px",fontFamily:"Sora,sans-serif"}}>Chargement...</div>;

  return (
    <>
      <style>{css}</style>
      <div className="config-wrap">
        <div className="config-card">
          <div className="config-header">
            <div className="config-logo"><div className="logo-r">R</div><div className="logo-name">Revyo</div></div>
            <div className="config-title">⚙️ Configuration</div>
            <div className="config-sub">Personnalisez votre espace restaurant</div>
          </div>

          {error && <div className="config-error">{error}</div>}
          {saved && <div className="config-success">Configuration sauvegardée ✓ Redirection...</div>}

          <div className="config-section">🏪 Informations du restaurant</div>
          <div className="config-field">
            <label>Nom du restaurant</label>
            <input name="nom" value={form.nom} onChange={handleChange} placeholder="Ex: Le Café Provençal" />
          </div>
          <div className="config-row">
            <div className="config-field">
              <label>Ville</label>
              <input name="ville" value={form.ville} onChange={handleChange} placeholder="Ex: Marseille" />
            </div>
            <div className="config-field">
              <label>Slug (URL)</label>
              <input name="slug" value={form.slug} onChange={handleChange} placeholder="cafe-provencal" />
            </div>
          </div>
          <div className="config-field">
            <label>Lien Google Review</label>
            <input name="google_maps_url" value={form.google_maps_url} onChange={handleChange} placeholder="https://g.page/r/..." />
            <div className="config-hint">Fiche Google → "Demander des avis" → Copier le lien</div>
          </div>

          <div className="config-section">📊 Métriques business</div>
          <div className="config-row">
            <div className="config-field">
              <label>Ticket moyen (€)</label>
              <input name="ticket_moyen" type="number" value={form.ticket_moyen} onChange={handleChange} placeholder="35" />
            </div>
            <div className="config-field">
              <label>Clients par jour (estimation)</label>
              <input name="couverts_semaine" type="number" value={form.couverts_semaine} onChange={handleChange} placeholder="80" />
              <div className="config-hint">Utilisé pour calculer votre ROI estimé</div>
            </div>
          </div>
          <div className="config-row">
            <div className="config-field">
              <label>Délai d'envoi du SMS</label>
              <select name="delai_envoi_minutes" value={form.delai_envoi_minutes ?? ''} onChange={handleChange}>
                <option value="0">Immédiatement après le scan</option>
                <option value="60">1 heure après</option>
                <option value="120">2 heures après</option>
                <option value="1440">Le lendemain matin</option>
              </select>
              <div className="config-hint">Les SMS envoyés 1h après ont un meilleur taux d'ouverture</div>
            </div>
            <div className="config-field">
              <label>Objectif d'avis (14 jours)</label>
              <input name="objectif_avis" type="number" value={form.objectif_avis ?? ''} onChange={handleChange} placeholder="7" />
              <div className="config-hint">Nombre d'avis que vous voulez générer sur la période</div>
            </div>
          </div>

          <div className="config-section">💬 Message SMS automatique</div>
          <div className="config-field">
            <textarea
              name="whatsapp_message"
              value={form.whatsapp_message}
              onChange={handleChange}
              rows={8}
              style={{
                width: '100%',
                minHeight: '150px',
                resize: 'vertical',
                padding: '12px',
                fontSize: '14px',
                lineHeight: '1.6',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontFamily: 'inherit',
                boxSizing: 'border-box'
              }}
              placeholder="Ex: Bonjour {prenom}, merci de votre visite ! Votre avis nous aide beaucoup..."
            />
            <div style={{
              textAlign: 'right',
              fontSize: '12px',
              color: (form.whatsapp_message?.length || 0) > 160 ? '#E8502A' : '#999',
              marginTop: '4px'
            }}>
              {form.whatsapp_message?.length || 0} / 160 caractères
              {(form.whatsapp_message?.length || 0) > 160 && ' ⚠️ SMS long (2 SMS facturés)'}
            </div>
            <div className="config-hint">Le lien Google sera ajouté automatiquement à la fin</div>
          </div>

          {form.slug && (
            <div className="config-link">
              <div className="config-section">📱 Votre lien client QR</div>
              <div className="config-url">
                <a href={`https://www.getrevyo.com/r/${form.slug}`} target="_blank" rel="noreferrer">
                  www.getrevyo.com/r/{form.slug}
                </a>
              </div>
            </div>
          )}

          <button className="config-btn" onClick={handleSubmit}>💾 Sauvegarder</button>

          <div className="config-nav">
            <button onClick={() => navigate("/dashboard")}>← Dashboard</button>
          </div>
        </div>
      </div>
    </>
  );
}

const css = `
@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');
*{box-sizing:border-box;margin:0;padding:0;}
body{font-family:'Sora',sans-serif;background:#F5F3EE;}
.config-wrap{min-height:100vh;padding:24px 20px;display:flex;justify-content:center;}
.config-card{width:100%;max-width:560px;}
.config-header{margin-bottom:24px;}
.config-logo{display:flex;align-items:center;gap:10px;margin-bottom:16px;}
.logo-r{width:36px;height:36px;background:#E8502A;border-radius:9px;display:flex;align-items:center;justify-content:center;color:white;font-weight:800;font-size:18px;}
.logo-name{font-size:20px;font-weight:800;letter-spacing:-0.5px;color:#18160F;}
.config-title{font-size:22px;font-weight:700;letter-spacing:-0.5px;color:#18160F;margin-bottom:4px;}
.config-sub{font-size:14px;color:#6A6858;}
.config-error{background:#FCECEA;color:#C8281C;padding:12px 16px;border-radius:8px;font-size:13px;margin-bottom:16px;}
.config-success{background:#E4F5EC;color:#0A7A45;padding:12px 16px;border-radius:8px;font-size:13px;margin-bottom:16px;font-weight:600;}
.config-section{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;color:#E8502A;margin:20px 0 12px;}
.config-field{margin-bottom:14px;}
.config-field label{display:block;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:0.07em;color:#ABA99F;margin-bottom:6px;}
.config-field input,.config-field textarea{width:100%;padding:12px 14px;border:1.5px solid #E8E4DA;border-radius:9px;font-size:14px;font-family:inherit;color:#18160F;outline:none;transition:border-color 0.15s;background:white;}
.config-field input:focus,.config-field textarea:focus{border-color:#E8502A;}
.config-field textarea{resize:vertical;min-height:80px;}
.config-hint{font-size:11px;color:#ABA99F;margin-top:4px;}
.config-row{display:grid;grid-template-columns:1fr 1fr;gap:12px;}
.config-link{margin-bottom:14px;}
.config-url{background:#FEF3E2;border-radius:9px;padding:12px 14px;font-size:13px;}
.config-url a{color:#E8502A;text-decoration:none;font-weight:500;}
.config-btn{width:100%;padding:15px;background:#E8502A;color:white;border:none;border-radius:9px;font-size:15px;font-weight:600;cursor:pointer;font-family:inherit;margin-top:8px;transition:all 0.15s;}
.config-btn:hover{background:#CC4220;}
.config-nav{margin-top:16px;display:flex;gap:10px;}
.config-nav button{flex:1;padding:12px;border:1.5px solid #E8E4DA;background:white;border-radius:9px;font-size:13px;color:#6A6858;cursor:pointer;font-family:inherit;}
.config-nav button:hover{background:#F5F3EE;}
`;